import { Component, inject, signal, OnInit, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { MenuService, MenuNode } from '../../services/menu';
import { DropdownService, DropdownItem } from '../../services/dropdown';
import { ToastService } from '../../services/toast';
import { PageTitleService } from '../../services/page-title';

type Tab = 'role' | 'user';
/** Which selection set a tree checkbox belongs to. */
type Kind = 'role' | 'grant' | 'deny';

@Component({
  selector: 'app-permission',
  standalone: true,
  imports: [CommonModule, FormsModule, SelectModule, ButtonModule, CheckboxModule],
  templateUrl: './permission.html',
  styleUrls: ['./permission.css'],
})
export class PermissionPage implements OnInit {
  private menu = inject(MenuService);
  private dropdown = inject(DropdownService);
  private toast = inject(ToastService);
  private platformId = inject(PLATFORM_ID);
  /** Page heading — pulled from the menu so it always matches the clicked item. */
  readonly pageTitle = inject(PageTitleService).title;

  tab = signal<Tab>('role');
  loading = signal(false);
  saving = signal(false);

  // All menus (the assignable universe), as a tree.
  menuTree = signal<MenuNode[]>([]);

  roleOptions = signal<DropdownItem[]>([]);
  userOptions = signal<DropdownItem[]>([]);

  // ---- role -> menus ----
  roleId = signal<number | null>(null);
  roleLoaded = signal(false);
  roleSelected = signal<Set<number>>(new Set());

  // ---- user -> grant/deny menus ----
  userId = signal<number | null>(null);
  userLoaded = signal(false);
  grantSelected = signal<Set<number>>(new Set());
  denySelected = signal<Set<number>>(new Set());

  // Read-only preview of the resulting effective menu for the selected user.
  previewLoading = signal(false);
  effectiveTree = signal<MenuNode[]>([]);

  ngOnInit() {
    if (!isPlatformBrowser(this.platformId)) return;
    this.loadTree();
    this.dropdown.roles().subscribe((o) => this.roleOptions.set(o));
    this.dropdown.users().subscribe((o) => this.userOptions.set(o));
  }

  setTab(t: Tab) {
    this.tab.set(t);
  }

  // ===================== helpers =====================
  private unwrap(res: any): any {
    return res?.data ?? res;
  }

  /** Coerce a list (of ids or objects) to a number[] of menu ids. */
  private toMenuIds(data: any): number[] {
    if (!Array.isArray(data)) return [];
    return data
      .map((x) => (typeof x === 'number' ? x : x?.menuId ?? x?.MenuId ?? x?.id))
      .filter((x): x is number => typeof x === 'number');
  }

  checked(kind: Kind, id: number): boolean {
    return this.setFor(kind)().has(id);
  }

  private setFor(kind: Kind) {
    return kind === 'role' ? this.roleSelected : kind === 'grant' ? this.grantSelected : this.denySelected;
  }

  onToggle(kind: Kind, id: number) {
    const sig = this.setFor(kind);
    const willCheck = !sig().has(id);
    const next = new Set(sig());
    willCheck ? next.add(id) : next.delete(id);
    sig.set(next);

    // Grant and Deny are a strict pair for a given menu: checking one clears the other,
    // and unchecking one auto-selects the other (a node is always grant XOR deny once touched).
    if (kind === 'grant') {
      willCheck ? this.removeFrom(this.denySelected, id) : this.addTo(this.denySelected, id);
    } else if (kind === 'deny') {
      willCheck ? this.removeFrom(this.grantSelected, id) : this.addTo(this.grantSelected, id);
    }
  }

  private removeFrom(sig: ReturnType<typeof signal<Set<number>>>, id: number) {
    if (!sig().has(id)) return;
    const next = new Set(sig());
    next.delete(id);
    sig.set(next);
  }

  private addTo(sig: ReturnType<typeof signal<Set<number>>>, id: number) {
    if (sig().has(id)) return;
    const next = new Set(sig());
    next.add(id);
    sig.set(next);
  }

  // ===================== load menus =====================
  loadTree() {
    this.loading.set(true);
    this.menu.getTree().subscribe({
      next: (res: any) => {
        this.loading.set(false);
        const data = this.unwrap(res);
        this.menuTree.set(Array.isArray(data) ? (data as MenuNode[]) : []);
      },
      error: () => this.loading.set(false),
    });
  }

  // ===================== role -> menus =====================
  loadRole() {
    const id = this.roleId();
    if (id == null) {
      this.toast.warning('Select a role first');
      return;
    }
    this.loading.set(true);
    this.menu.getRoleMenuIds(id).subscribe({
      next: (res: any) => {
        this.loading.set(false);
        this.roleSelected.set(new Set(this.toMenuIds(this.unwrap(res))));
        this.roleLoaded.set(true);
      },
      error: () => this.loading.set(false),
    });
  }

  saveRole() {
    const id = this.roleId();
    if (id == null) return;
    this.saving.set(true);
    this.menu.assignToRole({ roleId: id, menuIds: Array.from(this.roleSelected()) }).subscribe({
      next: (res: any) => {
        this.saving.set(false);
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to assign menus');
          return;
        }
        this.toast.success('Role menus saved');
      },
      error: () => this.saving.set(false),
    });
  }

  // ===================== user -> grant/deny =====================
  loadUser() {
    const id = this.userId();
    if (id == null) {
      this.toast.warning('Select a user first');
      return;
    }
    this.loading.set(true);
    this.menu.getUserOverrides(id).subscribe({
      next: (res: any) => {
        this.loading.set(false);
        const rows = this.unwrap(res);
        const grant = new Set<number>();
        const deny = new Set<number>();
        if (Array.isArray(rows)) {
          for (const r of rows) {
            const mid = r?.menuId ?? r?.MenuId ?? r?.id;
            const src = String(r?.source ?? r?.Source ?? '').toUpperCase();
            if (typeof mid !== 'number') continue;
            if (src === 'DENY') deny.add(mid);
            else if (src === 'GRANT') grant.add(mid);
          }
        }
        this.grantSelected.set(grant);
        this.denySelected.set(deny);
        this.userLoaded.set(true);
        this.loadPreview();
      },
      error: () => this.loading.set(false),
    });
  }

  saveUser() {
    const id = this.userId();
    if (id == null) return;
    this.saving.set(true);
    this.menu
      .assignToUser({
        userId: id,
        grantMenuIds: Array.from(this.grantSelected()),
        denyMenuIds: Array.from(this.denySelected()),
      })
      .subscribe({
        next: (res: any) => {
          this.saving.set(false);
          if (res?.success === false) {
            this.toast.error(res?.message || 'Failed to save overrides');
            return;
          }
          this.toast.success('User overrides saved');
          this.loadPreview();
        },
        error: () => this.saving.set(false),
      });
  }

  /** Refresh the read-only "resulting effective menu" preview for the selected user. */
  loadPreview() {
    const id = this.userId();
    if (id == null) return;
    this.previewLoading.set(true);
    this.menu.getByUserId(id).subscribe({
      next: (res: any) => {
        this.previewLoading.set(false);
        const data = this.unwrap(res);
        this.effectiveTree.set(Array.isArray(data) ? (data as MenuNode[]) : []);
      },
      error: () => this.previewLoading.set(false),
    });
  }
}
